import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Import, ChevronDown, ExternalLink } from 'lucide-react';
import { Tooltip } from '@getro/rombo';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { Box, Flex, Link } from 'rebass/styled-components';
import pluralize from 'pluralize';
import { useSourceDetails } from '../hooks/useSourceDetails';

export const SourceDetails = memo(
  ({ sources, trigger = null, placement = 'bottom-start', label = 'Source', triggerIcon = Import }) => {
    const sourcesToRender = useSourceDetails(sources);
    if (sourcesToRender.length === 0) {
      return null;
    }

    return (
      <Box
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <Dropdown
          placement={placement}
          trigger={
            trigger ?? (
              <Flex
                onClick={(event) => event.stopPropagation()}
                sx={{
                  cursor: 'pointer',
                  gap: '8px',
                  color: 'text.subtle',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  lineHeight: '16px',

                  svg: {
                    color: 'text.subtle',
                  },
                }}
              >
                <Box as={triggerIcon} size={16} />
                <Box>
                  {sourcesToRender.length} {pluralize(label, sourcesToRender.length)}
                </Box>
                <Box as={ChevronDown} size={16} />
              </Flex>
            )
          }
        >
          <Card
            width="fit-content"
            p="16px"
            minWidth="200px"
            maxWidth="480px"
            sx={{ gap: '8px', display: 'flex', flexDirection: 'column' }}
          >
            {sourcesToRender.map((source) => {
              const isResume = source.kind === 'job_application_resume';

              if (isResume) {
                return (
                  <Flex
                    key={source.id}
                    sx={{
                      gap: '16px',
                      alignItems: 'center',
                      color: 'text.main',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '140%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Flex sx={{ gap: '8px', alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                      <Box sx={{ color: 'neutral.400', flex: '0 0 auto' }} size={16} as={source.icon} />
                      <Tooltip
                        position="top"
                        content={source.title}
                        trigger={
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                          >
                            {source.title}
                          </Box>
                        }
                      />
                    </Flex>
                    {source.url && (
                      <Link
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open resume"
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          flex: '0 0 auto',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: 'text.subtle',
                          '&:hover': { color: 'text.dark' },
                        }}
                      >
                        <Box as={ExternalLink} size={16} />
                      </Link>
                    )}
                  </Flex>
                );
              }

              const row = (
                <Flex
                  sx={{
                    gap: '16px',
                    alignItems: 'center',
                    color: 'text.main',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    lineHeight: '140%',
                    justifyContent: 'space-between',
                  }}
                  key={source.id}
                >
                  <Flex sx={{ gap: '8px', alignItems: 'center', minWidth: 0, flex: '1 1 auto' }}>
                    <Box sx={{ color: 'neutral.400', flex: '0 0 auto' }} size={16} as={source.icon} />
                    <Box>{source.title}</Box>
                  </Flex>
                  {source.addedOn && (
                    <Box color="text.subtle" sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
                      {new Date(source.addedOn).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Box>
                  )}
                </Flex>
              );

              if (source.url) {
                return (
                  <Link
                    key={source.id}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      borderRadius: 'default',
                      '&:hover': { bg: 'neutral.20' },
                    }}
                  >
                    {row}
                  </Link>
                );
              }

              return row;
            })}
          </Card>
        </Dropdown>
      </Box>
    );
  },
);

SourceDetails.propTypes = {
  sources: PropTypes.array,
  trigger: PropTypes.node,
  placement: PropTypes.string,
  label: PropTypes.string,
  triggerIcon: PropTypes.elementType,
};

SourceDetails.defaultProps = {
  sources: [],
  trigger: null,
  placement: 'bottom-start',
  label: 'Source',
  triggerIcon: Import,
};
